import { useQuery } from '@tanstack/react-query'
import { api } from '../api/client'
import { fetchTsIds } from './shared/parallelFetch'
import { useToFill } from './shared/useToFill'

// Data pro skupinové produkty "Měsíční / Roční data – skupina" — paralelní sdata_ts fetch přes
// STANICE (na rozdíl od useMonthlyData/useYearlyData, kde je 1 stanice a smyčka přes tsId, tady je
// vnější smyčka přes ghIds a pro každou stanici se natáhne všech N tsID daného produktu).
// Viz doc/plans/station-group-monthly-yearly-phenomena.md sekce 4a.
//
// kind: 'mly' → stype=MONTH (timeKey = row.sid '01'..'12') + stype=YEAR (timeKey = 'YEAR')
//       'yly' → stype=YEAR  (timeKey = rok jako string z row.year)
//
// Vrací dataMap: Map<gh_id, Map<timeKey, Map<tsId, value>>>
export function useGroupPeriodData(kind, ghIds, tsIds, period, enabled = true) {
  const { year, yearFrom, yearTo } = period
  const isMonth = kind === 'mly'

  const queryKey = ['groupPeriodData', kind, [...ghIds].sort(), [...tsIds].sort(), isMonth ? year : `${yearFrom}-${yearTo}`]

  const { data, isLoading, isError, error } = useQuery({
    queryKey,
    queryFn: async () => {
      // celé odpovědi ({ data, to_fill }) — kvůli sběru to_fill; catch → prázdný obal
      const empty = { data: [], to_fill: [] }
      const perGh = await fetchTsIds(
        ghIds,
        async ghId => {
          if (isMonth) {
            const months = await fetchTsIds(
              tsIds,
              tsId => api.get(`/sdata_ts?ts_id=${tsId}&gh_id=${ghId}&year_from=${year}&year_to=${year}&stype=MONTH`).catch(() => empty),
            )
            const years = await fetchTsIds(
              tsIds,
              tsId => api.get(`/sdata_ts?ts_id=${tsId}&gh_id=${ghId}&year_from=${year}&year_to=${year}&stype=YEAR`).catch(() => empty),
            )
            return { months, years }
          }
          const years = await fetchTsIds(
            tsIds,
            tsId => api.get(`/sdata_ts?ts_id=${tsId}&gh_id=${ghId}&year_from=${yearFrom}&year_to=${yearTo}&stype=YEAR`).catch(() => empty),
          )
          return { years }
        },
      )
      // result: per ghId { months: row[][], years: row[][] }
      const result = perGh.map(g => ({
        months: (g.months ?? []).map(r => r?.data ?? []),
        years: (g.years ?? []).map(r => r?.data ?? []),
      }))
      const toFill = perGh.flatMap(g =>
        [...(g.months ?? []), ...(g.years ?? [])].flatMap(r => r?.to_fill ?? [])
      )
      return { result, toFill }
    },
    enabled: enabled && ghIds.length > 0 && tsIds.length > 0 && (isMonth ? !!year : !!yearFrom && !!yearTo),
    staleTime: 5 * 60 * 1000,
  })

  // resp.data je kompletní řada → nahradit [gi][bucket][ti].
  useToFill(queryKey, data, (result, resp, item) => {
    const gi = ghIds.indexOf(item.params?.gh_id)
    const ti = tsIds.indexOf(item.params?.ts_id)
    if (gi < 0 || ti < 0) return result
    const bucket = item.params?.stype === 'YEAR' ? 'years' : 'months'
    const next = result.slice()
    const g = { ...next[gi] }
    const arr = g[bucket].slice()
    arr[ti] = resp.data
    g[bucket] = arr
    next[gi] = g
    return next
  })

  const dataMap = new Map()
  if (data?.result) {
    ghIds.forEach((ghId, gi) => {
      const perGh = data.result[gi] ?? {}
      const timeMap = new Map()
      const put = (timeKey, tsId, value) => {
        const entry = timeMap.get(timeKey) ?? new Map()
        entry.set(tsId, value ?? null)
        timeMap.set(timeKey, entry)
      }
      tsIds.forEach((tsId, ti) => {
        if (isMonth) {
          ;(perGh.months?.[ti] ?? []).forEach(row => { if (row.sid) put(row.sid, tsId, row.value) })
          const yRows = perGh.years?.[ti] ?? []
          if (yRows.length > 0) put('YEAR', tsId, yRows[0]?.value)
        } else {
          ;(perGh.years?.[ti] ?? []).forEach(row => { if (row.year != null) put(String(row.year), tsId, row.value) })
        }
      })
      dataMap.set(ghId, timeMap)
    })
  }

  return { dataMap, isLoading, isError, error }
}
